import React from 'react';
import { fmtMin } from './engine/vreme';
import { activityLabel } from '../../utils/activities';
import { izvorInfo, LegendaIzvora } from '../common/IzvorAktivnosti';

const ACT_CLASS = { driving: 'driving', availability: 'availability', work: 'work', 'break/rest': 'rest', unknown: 'unknown' };
const SATI = Array.from({ length: 25 }, (_, h) => h);
/** naziv listića bez putanje i ekstenzije */
const kratakNaziv = (n) => String(n || '').split(/[\\/]/).pop().replace(/\.[a-z0-9]+$/i, '');

/** grupe izvora reda "U bazi": kartica / tahograf vozila, svaki listić ("Traka 1", "Traka 2" po vremenu), ručno — od–do na traci
    (odmor se ne broji kad grupa ima i drugih aktivnosti) */
const grupeBaze = (postojece) => {
    const po = new Map();
    [...postojece].sort((a, b) => a.start - b.start).forEach((s) => {
        const k = `${s.izvor || 'RUCNO'}|${s.listicId || ''}`;
        if (!po.has(k)) po.set(k, { kljuc: k, izvor: s.izvor || 'RUCNO', listic: s.listic, delovi: [] });
        po.get(k).delovi.push(s);
    });
    let traka = 0;
    const red = { KARTICA: 0, VOZILO: 1, LISTIC: 2, RUCNO: 3 };
    return [...po.values()].map((g) => {
        const rad = g.delovi.filter((s) => s.type !== 'break/rest');
        const opseg = rad.length ? rad : g.delovi;
        return { ...g, od: Math.min(...opseg.map((s) => s.start)), do: Math.max(...opseg.map((s) => s.end)) };
    }).sort((a, b) => (red[a.izvor] ?? 4) - (red[b.izvor] ?? 4) || a.od - b.od)
        .map((g) => ({ ...g, naziv: g.izvor === 'LISTIC' ? `Traka ${++traka}` : izvorInfo(g.izvor).label.replace(/^./, (c) => c.toUpperCase()) }));
};

/* Odmotana traka aktivnosti (0–24 h, u prikaznom vremenu — UTC kad je poznat datum) i ispod nje aktivnosti listića — operater odmah vidi
   da li prepoznato odgovara tragu pisaljke. Klik na traku postavlja početak aktivnosti.
   `postojece`: aktivnosti koje su već u bazi u periodu listića, osim odmora i nepoznatog (plan.js postojeceNaTraci) — poseban šrafiran
   red "U bazi" sa trakom izvora i grupama (Kartica / Traka 1, 2 / Ručno, od–do); samo prikaz, ne unose se sa listićem. */
export default function TraceStrip({ src, segments, ulaganje, srednja, onUlaganje, pomak = 0, postojece = [] }) {
    const pct = (m) => `${(m / 1440) * 100}%`;
    const click = (e) => {
        if (!onUlaganje) return;
        const r = e.currentTarget.getBoundingClientRect();
        onUlaganje(Math.max(0, Math.min(1439, Math.round(((e.clientX - r.left) / r.width) * 1440))));
    };
    const grupe = postojece.length ? grupeBaze(postojece) : [];
    const opis = (s) => (s.izvor === 'LISTIC' && s.listic ? `listić ${s.listic}` : izvorInfo(s.izvor).label);
    return (
        <div className="trace-strip">
            <div className="trace-hours" aria-hidden="true">
                {SATI.map((h) => <span key={h} style={{ left: pct(h * 60) }}>{h}</span>)}
            </div>
            <div className="trace-img" onClick={click} title={onUlaganje ? 'Klik postavlja početak aktivnosti (kada tahograf počinje da beleži na listiću)' : undefined}>
                {/* traka sa listića je u lokalnom vremenu: pomera se za pomak (dve kopije zbog prelaza preko ponoći) */}
                {src && (
                    <div className="trace-img-pomak" style={{ transform: `translateX(${(-((pomak % 1440) + 1440) % 1440 / 2880) * 100}%)` }}>
                        <img src={src} alt="Odmotana traka aktivnosti sa listića" draggable={false} />
                        <img src={src} alt="" aria-hidden="true" draggable={false} />
                    </div>
                )}
                {SATI.map((h) => <i key={h} className="trace-tick" style={{ left: pct(h * 60) }} />)}
                <i className="trace-insert" style={{ left: pct(ulaganje) }} title={`Početak aktivnosti ${fmtMin(ulaganje)}`} />
            </div>
            <div className="trace-acts" onClick={click}>
                {segments.map((s, i) => (
                    <span key={i} className={`trace-seg ${s.type ? ACT_CLASS[s.type === 'availability' ? srednja : s.type] : 'none'}`}
                        style={{ left: pct(s.start), width: pct(s.end - s.start) }} title={`${fmtMin(s.start)}–${fmtMin(s.end)}`} />
                ))}
            </div>
            {postojece.length > 0 && (
                <>
                    <div className="trace-baza" aria-label="Aktivnosti koje su već u bazi — nisu sa ovog listića">
                        {postojece.map((s, i) => (
                            <span key={i} className={`trace-seg ${ACT_CLASS[s.type] || 'unknown'}`} style={{ left: pct(s.start), width: pct(s.end - s.start) }}
                                title={`Već u bazi (${opis(s)}), nije sa ovog listića: ${activityLabel(s.type)} ${fmtMin(s.start)}–${fmtMin(s.end)}`} />
                        ))}
                        <span className="trace-baza-naslov">U bazi — nije sa listića</span>
                    </div>
                    <div className="trace-izvor" aria-hidden="true">
                        {postojece.map((s, i) => (
                            <span key={i} className={`izv-seg ${izvorInfo(s.izvor).cls}`} style={{ left: pct(s.start), width: pct(s.end - s.start) }} />
                        ))}
                    </div>
                    <div className="izvori-dana trace-grupe" role="list" aria-label="Izvori aktivnosti u bazi">
                        {grupe.map((g) => {
                            const info = izvorInfo(g.izvor);
                            return (
                                <span key={g.kljuc} role="listitem" className={`izvor-grupa ${info.cls}`}
                                    title={g.izvor === 'LISTIC' ? `${g.naziv} — listić ${g.listic || ''}` : `${g.naziv}${info.cls === 'kartica' ? ' — ne menja se' : ''}`}>
                                    <i className={info.ikona} aria-hidden="true" />
                                    <b>{g.naziv}</b>
                                    {g.izvor === 'LISTIC' && g.listic && <span className="izvor-listic">{kratakNaziv(g.listic)}</span>}
                                    <span className="num">{fmtMin(g.od)}–{g.do >= 1440 ? '24:00' : fmtMin(g.do)}</span>
                                    {info.cls === 'kartica' && <i className="pi pi-lock" aria-hidden="true" />}
                                </span>
                            );
                        })}
                    </div>
                    <LegendaIzvora />
                </>
            )}
        </div>
    );
}
